import React, { useEffect, useRef } from "react";
import { formatBoardStatLabel } from "../boardStatLabels";

const StatSelection = (props) => {
  const statKeys = Object.keys(props.stats);

  const scrollRef = useRef(null);
  const hasCenteredInitialStatRef = useRef(false);
  const dragStateRef = useRef({
    isDown: false,
    moved: false,
    startX: 0,
    scrollLeft: 0,
    suppressClick: false,
  });

  useEffect(() => {
    const sliderList = scrollRef.current;
    if (!sliderList) {
      return undefined;
    }

    const selectedButton = sliderList.querySelector('.select-btn.selected');
    if (!selectedButton) {
      return undefined;
    }

    const animationFrame = window.requestAnimationFrame(() => {
      const sliderRect = sliderList.getBoundingClientRect();
      const selectedRect = selectedButton.getBoundingClientRect();
      const targetLeft = sliderList.scrollLeft
        + selectedRect.left
        - sliderRect.left
        - ((sliderRect.width - selectedRect.width) / 2);
      const maxLeft = sliderList.scrollWidth - sliderList.clientWidth;
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const behavior = hasCenteredInitialStatRef.current && !prefersReducedMotion ? 'smooth' : 'auto';

      sliderList.scrollTo({
        left: Math.max(0, Math.min(targetLeft, maxLeft)),
        behavior,
      });
      hasCenteredInitialStatRef.current = true;
    });

    return () => window.cancelAnimationFrame(animationFrame);
  }, [props.stat, statKeys.join('|')]);

  const handleMouseDown = (e) => {
    if (e.button !== 0 || !scrollRef.current) {
      return;
    }

    dragStateRef.current.isDown = true;
    dragStateRef.current.moved = false;
    dragStateRef.current.startX = e.pageX;
    dragStateRef.current.scrollLeft = scrollRef.current.scrollLeft;
    scrollRef.current.classList.add('dragging');
  };

  const stopDrag = () => {
    const dragState = dragStateRef.current;

    dragState.isDown = false;
    scrollRef.current?.classList.remove('dragging');

    if (dragState.moved) {
      dragState.suppressClick = true;
      window.setTimeout(() => {
        dragStateRef.current.suppressClick = false;
        dragStateRef.current.moved = false;
      }, 0);
    }
  };

  const handleMouseMove = (e) => {
    const dragState = dragStateRef.current;
    if (!dragState.isDown || !scrollRef.current) {
      return;
    }

    const walk = (e.pageX - dragState.startX) * 1.5;
    if (Math.abs(walk) > 4) {
      dragState.moved = true;
      e.preventDefault();
    }

    scrollRef.current.scrollLeft = dragState.scrollLeft - walk;
  };

  const handleClickCapture = (e) => {
    if (!dragStateRef.current.suppressClick) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div
      ref={scrollRef}
      style={{
        overflowX: 'auto',
        whiteSpace: 'nowrap',
        WebkitOverflowScrolling: 'touch',
        msOverflowStyle: 'none', // IE and Edge
        scrollbarWidth: 'none', // Firefox
        marginLeft: 0,
        marginRight: 0,
        cursor: 'grab',
        textAlign: 'left',
      }}
      className="hide-scrollbar"
      onClickCapture={handleClickCapture}
      onMouseDown={handleMouseDown}
      onMouseLeave={stopDrag}
      onMouseUp={stopDrag}
      onMouseMove={handleMouseMove}
    >
      {statKeys.map((stat) => (
        <button
          key={stat}
          onClick={props.onClick}
          className={'select-btn' + (props.stat === stat ? ' selected' : '')}
          data-prop='stat'
          value={stat}
          style={{ display: 'inline-block', marginRight: 12 }}
        >
          {formatBoardStatLabel(stat, props.stats[stat], { popularLabel: props.popularLabel })}
        </button>
      ))}
    </div>
  );
};

export default StatSelection;
